/**
 * App Component
 * Entry point of the app
 * This is where the high level components like redux store, routing, error handling are initialized
 * */
import "react-app-polyfill/ie11"
import "react-app-polyfill/stable"
import React, { Component } from "react"
import { Provider } from "react-redux"
import { ConnectedRouter } from "react-router-redux"
import store, { history, persistor } from "./store"
import WrapperLayout from "./layouts/WrapperLayout/container"
import "./services/interceptors"
import "normalize.css/normalize.css"
import GlobalStyle from "./styles/globals"
import AppRoutes from "./routes/AppRoutes/container"
import ErrorWrapper from "components/ErrorWrapper"
import { getCurrentSize, getQueryParameters } from "utils/utils"
import { PersistGate } from "redux-persist/integration/react"
import AppLoader from "components/AppLoader"
import "./styles/tippy.css"
import { initialiseGA, removeGoogleAnalyticsCookies } from "utils/GA"
import CookieBanner from "components/CookieBanner/CookieBanner"
import ManageCookies from "components/CookieBanner/ManageCookies"
import UncaughtError from "components/UncaughtError"
import { HelmetProvider } from "react-helmet-async"
import { redirectLink } from "utils/clientUtils"

// Size context is a way of knowing what type of screen we are currently on (xs, sm, md, lg, xl)
// Import it to screens to use the value
export const SizeContext = React.createContext("sm")

class App extends Component {
  state = {
    size: "sm",
    showCookieBanner: true,
    showManageCookiesModal: false,
  }

  closeManageCookies = () => {
    this.setState({
      showManageCookiesModal: false,
      showCookieBanner: true,
    })
  }

  openManageCookies = () => {
    this.setState({
      showManageCookiesModal: true,
      showCookieBanner: false,
    })
  }

  componentDidCatch(error, info) {
    // You can also log the error to an error reporting service
    this.setState({ hasError: true, error, stack: error.stack })
  }

  componentDidMount() {
    // persistor.purge();
    removeGoogleAnalyticsCookies()
    window.addEventListener("resize", this.reCalculateSize)
    redirectLink()
    this.reCalculateSize()

    const query = getQueryParameters(window.location)

    initialiseGA()

    const showCookieBanner =
      localStorage.getItem("cookiesAccepted") === null && !query.print

    this.setState({ showCookieBanner: showCookieBanner })
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.reCalculateSize)
  }

  reCalculateSize = () => {
    this.setState({ size: getCurrentSize() })
  }

  acceptAllCookies = () => {
    localStorage.setItem("cookiesAccepted", "true")
    window.location.reload(false)
  }

  rejectAllCookies = () => {
    localStorage.setItem("cookiesAccepted", "false")
    window.location.reload(false)
  }

  saveCookiePreferences = acceptCookies => {
    if (acceptCookies) {
      this.acceptAllCookies()
    } else {
      this.rejectAllCookies()
    }
  }

  render() {
    const { showCookieBanner, showManageCookiesModal } = this.state
    if (this.state.hasError) {
      return <UncaughtError error={this.state.error} stack={this.state.stack} />
    }

    return (
      <Provider store={store}>
        <PersistGate loading={<AppLoader />} persistor={persistor}>
          <HelmetProvider>
            <ConnectedRouter history={history}>
              <WrapperLayout>
                <GlobalStyle />
                <ErrorWrapper store={store}>
                  <SizeContext.Provider value={this.state.size}>
                    <AppRoutes />
                  </SizeContext.Provider>
                </ErrorWrapper>
              </WrapperLayout>
            </ConnectedRouter>
            <CookieBanner
              display={showCookieBanner}
              acceptAllCookies={this.acceptAllCookies}
              rejectAllCookies={this.rejectAllCookies}
              openManageCookies={this.openManageCookies}
            />
            <ManageCookies
              display={showManageCookiesModal}
              closeManageCookies={this.closeManageCookies}
              saveCookiePreferences={this.saveCookiePreferences}
            />
          </HelmetProvider>
        </PersistGate>
      </Provider>
    )
  }
}

export default App
